import {
  lastSetXTokenTimeStorage,
  reflectionStorage,
  store,
  xRbacTokenStorage,
} from '@/store'
import { getTheme } from '@/utils/theme'
import { Form, FormItem, Input, Password, Submit } from '@formily/antd'
import { createForm } from '@formily/core'
import { createSchemaField } from '@formily/react'
import { moment } from '@seiue/moment'
import { Toast } from '@seiue/ui'
import { isValidPhoneNumber } from '@seiue/util'
import { useAtom } from 'jotai'
import {
  LoginReq,
  User,
  UserDomainEnum,
  authApi,
} from 'packages/sdks-next/growp'
import { AnyValueObj } from 'packages/types'
import { getUrlParams } from 'packages/utils/utils'
import React, { useEffect } from 'react'
import styled from 'styled-components'

import { VerifyCode } from '../VerifyCode'
import { reminderIdAtom } from '../atom'
import { getFormalUserJumpUrl } from '../user/utils'

import { usePhoneSchema } from './schema'

const form = createForm({
  validateFirst: true,
})

const SchemaField = createSchemaField({
  components: {
    FormItem,
    Input,
    Password,
    VerifyCode,
  },
})

/**
 * 验证码登录
 *
 * @param param - 参数
 * @param param.isPreview - 是否是预览
 * @param param.activeKey - 活动键
 * @param param.setUser - 设置用户
 * @param param.setLoginParam - 设置登录参数
 * @returns 组件
 */
export const CodeLogin: React.FC<{
  isPreview?: boolean
  activeKey: string
  setUser: (v: User) => void
  setLoginParam: (v: AnyValueObj) => void
}> = ({ isPreview, activeKey, setUser, setLoginParam }) => {
  const theme = getTheme()
  const [reminderId] = useAtom(reminderIdAtom)
  const phoneSchema = usePhoneSchema(isPreview)

  const openLogin = {
    clientId: getUrlParams('client_id'),
    scope: getUrlParams('scope'),
    state: getUrlParams('state'),
    redirectUri: getUrlParams('redirect_uri'),
    responseType: getUrlParams('response_type'),
    nonce: getUrlParams('nonce'),
  }

  useEffect(() => {
    form.reset()
  }, [activeKey])

  return (
    <Form
      form={form}
      layout="vertical"
      size="large"
      onAutoSubmit={async (val: LoginReq) => {
        if (
          isPreview ||
          !isValidPhoneNumber(val.phone || '') ||
          !val.reminderId
        ) {
          return
        }

        try {
          const { data: reflection } = await authApi.login(
            UserDomainEnum.Formal,
            {
              ...val,
              reminderId: reminderId,
              password: val.reminderId,
              // schoolId: Number(schoolId),
              // 暂时只有一个学校使用，后期可以扩展
              // NEED TO FIX HACK
              schoolId: 3,
              openLogin,
            },
            { omitAuthorizationHeader: true }
          )

          // 当前账号绑定了多个角色
          if (reflection.reflections && reflection.reflections?.length > 1) {
            setUser(reflection)
            setLoginParam({
              ...val,
              reminderId: reminderId,
              password: val.reminderId,
              // schoolId: Number(schoolId),
              // 暂时只有一个学校使用，后期可以扩展
              // NEED TO FIX HACK
              schoolId: 3,
              openLogin,
            })
          } else {
            store.set(reflectionStorage, reflection)
            authApi
              .checkDataToken()
              .then(({ data }) => {
                store.set(xRbacTokenStorage, data)
                store.set(lastSetXTokenTimeStorage, moment().toISOString())
              })
              // 不管 checkDataToken 是否成功，都进行下一步界面，不影响主流程
              .finally(() => {
                // 如果返回了跳转地址，直接重定向到指定页面
                if (reflection.redirectUri) {
                  location.href = reflection.redirectUri
                  return
                }

                location.href = getFormalUserJumpUrl(reflection.role)
              })
          }
        } catch (e: any) {
          const { data } = e.response
          Toast.error(data?.message)
        }
      }}
      style={{ marginTop: '16px' }}
    >
      <ItemWrapper>
        <div>
          <SchemaField schema={phoneSchema} />
        </div>
        <Submit
          block={true}
          size="large"
          style={{
            borderRadius: 8,
            backgroundColor: theme.brand._1,
            color: '#fff',
          }}
        >
          {'登录'}
        </Submit>
      </ItemWrapper>
    </Form>
  )
}

const ItemWrapper = styled.div`
  height: 250px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`
